import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {TitleBreadcrumbsChangedAction, TitleChangedAction} from '../../../core/actions/title.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {SortTable, SortTableRow} from '../../../shared/models/sort-table/types';
import {SearchService} from '../../../core/services/search.service';
import {UserInvitation} from '../../../shared/models/invitations';
import {
  SortTableCellStatusDot,
  SortTableCellTextPill,
  SortTableCellTitleDescription,
} from '../../../shared/models/sort-table/cell-classes';
import {BehaviorSubject, Subscription} from 'rxjs';
import {UsersService} from '../../../core/services/users.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {UtilsService} from '../../../core/services/utils.service';
import {HttpErrorResponse} from '@angular/common/http';
import {UrlService} from '../../../core/services/url.service';
import {FormsModule} from '@angular/forms';
import {SpinnerComponent} from '../../components/spinner/spinner.component';
import {PillComponent} from '../../components/pill/pill.component';
import {SortTableComponent} from '../../components/sort-table/sort-table.component';
import {NgClass} from '@angular/common';

const COL_IDX_NAME = 0;
const COL_IDX_STATUS = 1;
const COL_IDX_CREATED = 2;

@Component({
  selector: 'motn-invitations',
  templateUrl: './invitations.component.html',
  styleUrls: ['./invitations.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    SpinnerComponent,
    PillComponent,
    SortTableComponent,
    NgClass,
  ],
})
export class InvitationsComponent implements OnInit, OnDestroy {

  sendingInvite: boolean = false;
  inviteeName: string = '';
  inviteeEmail: string = '';

  table$ = new BehaviorSubject<SortTable>(null);
  rows: SortTableRow[];

  invitations: UserInvitation[];
  searchTerm: string = '';

  private searchSub: Subscription;


  constructor(
    private searchService: SearchService,
    private urlService: UrlService,
    private usersService: UsersService,
    private shadeTools: ShadeToolsService,
    private store: Store<AppState>,
  ) {
  }


  ngOnInit(): void {
    this.updateTitle();
    this.searchService.enableSearch();
    this.fetchInvites();

    this.searchSub = this.urlService.searchTerm$.subscribe(
      (term) => {
        this.searchTerm = term;
        this.updateTable();
      },
    );
  }


  ngOnDestroy() {
    this.searchSub?.unsubscribe();
  }


  sendInvite() {
    if (!this.inviteeName.trim() || !this.inviteeEmail) {
      this.shadeTools.errorDialog('You must fill in both a name and an email address for the person you wish to invite.');
    }

    this.sendingInvite = true;

    this.usersService.createUserInvitation$(this.inviteeName, this.inviteeEmail).subscribe(
      () => {
        this.shadeTools.simpleOkDialog('Invitation Sent', `Your invitation was sent successfully! You'll receive an email if the invitee creates an account using your invitation.`);
        this.fetchInvites();

        this.inviteeName = '';
        this.inviteeEmail = '';
        this.sendingInvite = false;
      },
      (err: HttpErrorResponse) => {
        const errorMessage = UtilsService.extractErrorMessage(err.error);
        if (errorMessage.includes('mail') || errorMessage.includes('email')) {
          this.shadeTools.errorDialog('The email address you provided is not valid.');
        } else {
          this.shadeTools.errorDialog();
        }

        this.sendingInvite = false;
      },
    );
  }


  private fetchInvites() {
    this.usersService.findUserInvitations$().subscribe(
      (invites) => {
        this.invitations = invites;
        this.updateTable();
      },
      () => {
        this.shadeTools.errorDialog('Your current invitations could not be retrieved. Try again later.');
      },
    );
  }


  private updateTable() {
    if (!this.invitations) {
      return;
    }

    const colNameWidth = 150;
    const colStatusWidth = 160;
    const colCreatedWidth = 260;

    const table: SortTable = {
      css: {
        'min-width': (colNameWidth + colStatusWidth + colCreatedWidth) + 'px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 20,
        startOnPage: 1,
        maxPageOptions: 12,
      },
    };

    {
      table.columns[COL_IDX_NAME] = {
        name: 'Name',
        css: {
          'width': `calc(100% - ${colStatusWidth + colCreatedWidth}px)`,
        },
        sortingFn: SortTableCellTitleDescription.sortingFnTitle,
      };

      table.columns[COL_IDX_STATUS] = {
        name: 'Status',
        css: {
          'width': colStatusWidth + 'px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellStatusDot.sortingFnStatusLabel,
      };

      table.columns[COL_IDX_CREATED] = {
        name: 'Date/Time Sent',
        css: {
          'width': colCreatedWidth + 'px',
          'justify-content': 'end',
        },
        sortingFn: SortTableCellTextPill.sortingFnCustomSortValue,
        sortDefault: 'down',
      };
    }

    let invites: UserInvitation[] = this.invitations;

    if (this.searchTerm) {
      invites = this.searchService.searchInvitations(this.searchTerm, invites);
    }

    for (let invite of invites) {
      const row: SortTableRow = {
        context: '' + invite.Code,
        cells: [],
      };

      this.upsertRowData(row, invite);
      table.rows.push(row);
    }

    this.searchService.setResultQty(table.rows.length);
    this.rows = table.rows;
    this.table$.next(table);
  }


  /**
   *
   * @param row
   * @param invitation
   * @private
   */
  private upsertRowData(row: SortTableRow, invitation: UserInvitation) {
    const monoTime = UtilsService.makeMonospaceTime(invitation.Created);

    row.cells[COL_IDX_NAME] = new SortTableCellTitleDescription(invitation.InviteeName, invitation.InviteeEmail);
    row.cells[COL_IDX_STATUS] = new SortTableCellStatusDot(invitation.Used ? 'green' : 'yellow', invitation.Used ? 'Invitation Used' : 'Invitation Sent');
    row.cells[COL_IDX_CREATED] = new SortTableCellTextPill(monoTime, '', true).addSortValue(invitation.Created);
  }


  /**
   *
   * @private
   */
  private updateTitle() {
    this.store.dispatch(new TitleChangedAction(
      '',
      'Invitations',
    ));

    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [
        {
          text: 'Invitations',
        },
      ],
    ));
  }
}
